import { makeStyles } from '@material-ui/core/styles'

type StyleProps = { isDarkMode: boolean }

/**
 * Shared styles for Asset sub-flyout edit forms.
 * Matches the parent AssetEditForm design token set exactly.
 */
const useAssetFormStyles = makeStyles((theme) => ({
  /* ── Scroll wrapper ───────────────────────────────── */
  scrollBody: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto' as const,
    padding: '16px 16px 0',
  },

  /* ── Section card ─────────────────────────────────── */
  section: {
    padding: '14px 16px',
    borderRadius: 10,
    border: (props: StyleProps) =>
      props.isDarkMode ? '1px solid #333' : '1px solid #E5E7EB',
    backgroundColor: (props: StyleProps) =>
      props.isDarkMode ? theme.palette.background.paper : '#fff',
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.07em',
    textTransform: 'uppercase' as const,
    color: (props: StyleProps) =>
      props.isDarkMode ? '#6B7280' : '#9CA3AF',
    marginBottom: 12,
  },

  /* ── Field wrapper ────────────────────────────────── */
  fieldGroup: {
    marginBottom: 10,
  },
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
  },

  /* ── Field label ──────────────────────────────────── */
  fieldLabel: {
    fontSize: 12,
    fontWeight: 500,
    color: (props: StyleProps) =>
      props.isDarkMode ? theme.palette.text.secondary : '#374151',
    marginBottom: 4,
    display: 'block',
  },
  required: {
    color: '#EF4444',
    fontWeight: 600,
  },

  /* ── Text / number / date input ───────────────────── */
  input: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 7,
      height: 38,
      background: (props: StyleProps) =>
        props.isDarkMode ? theme.palette.background.paper : '#FAFAFA',
    },
    '& .MuiOutlinedInput-root.Mui-focused': {
      background: (props: StyleProps) =>
        props.isDarkMode ? theme.palette.background.paper : '#fff',
    },
    '& .MuiOutlinedInput-input': {
      padding: '9px 12px',
      fontSize: 13,
      color: (props: StyleProps) =>
        props.isDarkMode ? theme.palette.text.primary : '#111827',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: (props: StyleProps) =>
        props.isDarkMode ? theme.palette.divider : '#E5E7EB',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: (props: StyleProps) =>
        props.isDarkMode ? theme.palette.text.secondary : '#94A3B8',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: (props: StyleProps) =>
        props.isDarkMode ? theme.palette.primary.main : '#163A64',
      borderWidth: 1,
    },
    '& input::placeholder': {
      color: (props: StyleProps) =>
        props.isDarkMode ? theme.palette.text.secondary : '#9CA3AF',
      opacity: 1,
      fontSize: 13,
    },
    '& .MuiOutlinedInput-root.Mui-disabled': {
      background: (props: StyleProps) =>
        props.isDarkMode ? theme.palette.divider : '#F1F5F9',
    },
  },

  /* ── Multiline textarea ───────────────────────────── */
  textarea: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 7,
      background: (props: StyleProps) =>
        props.isDarkMode ? theme.palette.background.paper : '#FAFAFA',
    },
    '& .MuiOutlinedInput-root.Mui-focused': {
      background: (props: StyleProps) =>
        props.isDarkMode ? theme.palette.background.paper : '#fff',
    },
    '& .MuiOutlinedInput-input': {
      padding: '9px 12px',
      fontSize: 13,
      color: (props: StyleProps) =>
        props.isDarkMode ? theme.palette.text.primary : '#111827',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: (props: StyleProps) =>
        props.isDarkMode ? theme.palette.divider : '#E5E7EB',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: (props: StyleProps) =>
        props.isDarkMode ? theme.palette.text.secondary : '#94A3B8',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: (props: StyleProps) =>
        props.isDarkMode ? theme.palette.primary.main : '#163A64',
      borderWidth: 1,
    },
    '& textarea::placeholder': {
      color: (props: StyleProps) =>
        props.isDarkMode ? theme.palette.text.secondary : '#9CA3AF',
      opacity: 1,
      fontSize: 13,
    },
    '& .MuiOutlinedInput-root.Mui-disabled': {
      background: (props: StyleProps) =>
        props.isDarkMode ? theme.palette.divider : '#F1F5F9',
    },
  },

  /* ── Select (single) ──────────────────────────────── */
  select: {
    width: '100%',
    '& .MuiOutlinedInput-root, &.MuiOutlinedInput-root': {
      borderRadius: 7,
      height: 38,
      background: (props: StyleProps) =>
        props.isDarkMode ? theme.palette.background.paper : '#FAFAFA',
    },
    '& .MuiSelect-outlined': {
      padding: '9px 12px',
      fontSize: 13,
      color: (props: StyleProps) =>
        props.isDarkMode ? theme.palette.text.primary : '#111827',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: (props: StyleProps) =>
        props.isDarkMode ? theme.palette.divider : '#E5E7EB',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: (props: StyleProps) =>
        props.isDarkMode ? theme.palette.text.secondary : '#94A3B8',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: (props: StyleProps) =>
        props.isDarkMode ? theme.palette.primary.main : '#163A64',
      borderWidth: 1,
    },
  },

  /* ── Select (multi) ───────────────────────────────── */
  multiSelect: {
    width: '100%',
    '& .MuiOutlinedInput-root, &.MuiOutlinedInput-root': {
      borderRadius: 7,
      minHeight: 38,
      background: (props: StyleProps) =>
        props.isDarkMode ? theme.palette.background.paper : '#FAFAFA',
    },
    '& .MuiSelect-outlined': {
      padding: '9px 12px',
      fontSize: 13,
      color: (props: StyleProps) =>
        props.isDarkMode ? theme.palette.text.primary : '#111827',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: (props: StyleProps) =>
        props.isDarkMode ? theme.palette.divider : '#E5E7EB',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: (props: StyleProps) =>
        props.isDarkMode ? theme.palette.text.secondary : '#94A3B8',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: (props: StyleProps) =>
        props.isDarkMode ? theme.palette.primary.main : '#163A64',
      borderWidth: 1,
    },
  },

  /* ── Action bar (sticky footer) ──────────────────── */
  actionBar: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    borderTop: (props: StyleProps) =>
      props.isDarkMode ? '1px solid #333' : '1px solid #E5E7EB',
    marginTop: 4,
    flexShrink: 0,
    backgroundColor: (props: StyleProps) =>
      props.isDarkMode ? '#2d2d2d' : '#ffffff',
  },
  /* Push save/cancel to the right when delete is present */
  actionBarSpacer: {
    flex: 1,
  },
  actionBarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
}))

export type AssetFormClasses = ReturnType<typeof useAssetFormStyles>
export { useAssetFormStyles }
export default useAssetFormStyles
